/**
 * Validate + normalize slots for draw_rectangle
 * Produces a ready payload:
 * {
 *   cell, layerId,
 *   x, y, w, h,
 *   units: "um",
 *   rotation: 0,
 *   repeat: { nx, ny, dx, dy }
 * }
 *
 * NOTE: throws UI errors as: { ui: true, code: "layer_not_found" | "layer_locked" | "zero_size" }
 */

import layerLibrary from "../../canvas/LayerLibrary";
import GridSystem from "../../canvas/gridSystem";

/* ───────────────────────────── Public ───────────────────────────── */
export function validateDelete(state){
  const d = state.slots?.deletion || {};
  const cell = d.cell;
  if (!cell) throw uiError("cell_required");

  let layerId = null;
  if (state.slots?.layer) {
    layerId = resolveLayerId(state.slots.layer); // existing function
  }

  // region normalize
  let region = null;
  if (d.region?.p1 && d.region?.p2) {
    const p1 = toUnits([d.region.p1[0], d.region.p1[1]], "um");
    const p2 = toUnits([d.region.p2[0], d.region.p2[1]], "um");
    region = {
      x1: Math.min(p1.x, p2.x), y1: Math.min(p1.y, p2.y),
      x2: Math.max(p1.x, p2.x), y2: Math.max(p1.y, p2.y),
    };
  }

  // size filter (optional)
  let size = null;
  if (d.size?.w && d.size?.h) {
    size = { w: Number(d.size.w), h: Number(d.size.h), units: "um" };
  }

  const ids = Array.isArray(d.ids) ? d.ids.filter(Number.isFinite) : null;
  const selected = !!d.selected;

  return { cell, layerId, region, size, ids, selected };
}


export function validateAndNormalize(state) {
  const { cell } = state.slots || {};
  const layerId = resolveLayerId(state.slots?.layer);
  const { x, y, w, h, units } = normalizeGeometry(state.slots);

  // rotation (optional)
  const rotation = Number.isFinite(state.slots?.rotation) ? state.slots.rotation : 0;

  // repeat (optional)
  const repeat = normalizeRepeat(state.slots?.repeat);

  return { cell, layerId, x, y, w, h, units, rotation, repeat };
}

/* ───────────────────────────── Layer resolve ───────────────────────────── */

function resolveLayerId(layer) {
  if (!layer) throw uiError("layer_not_found");

  // Already "L:D" ?
  if (typeof layer === "string" && /^\d+\s*[:/]\s*\d+$/.test(layer)) {
    const [L, D] = layer.split(/[:/]/).map((s) => s.trim());
    const lid = `${Number(L)}:${Number(D)}`;
    const found = layerLibrary.getLayerById?.(lid);
    if (!found) throw uiError("layer_not_found");
    ensureLayerWritable(found);
    return lid;
  }

  // Canonical alias (e.g., "m1", "nw1") -> map by name
  const byName =
    layerLibrary.getLayerByName?.(String(layer).toUpperCase()) ||
    layerLibrary.getLayerByName?.(String(layer).toLowerCase());
  if (byName) {
    ensureLayerWritable(byName);
   return `${byName.layer_number}:${byName.datatype_number ?? 0}`;
  }

  // Try fuzzy by displayName
  const all = layerLibrary.getAllLayers?.() || [];
  const hit = all.find(
    (L) =>
      eq(L?.name, layer) ||
      eq(L?.displayName, layer) ||
      eq(L?.shortName, layer)
  );
  if (hit) {
    ensureLayerWritable(hit);
    return `${hit.layer_number}:${hit.datatype_number ?? 0}`;
  }

  throw uiError("layer_not_found");
}

function ensureLayerWritable(layer) {
  const ln = layer.layer_number ?? layer.layer;
  const dt = layer.datatype_number ?? layer.datatype ?? 0;
  if (layerLibrary.isLayerLocked?.(ln, dt)) throw uiError("layer_locked");
}

function eq(a, b) {
  if (!a || !b) return false;
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
}

/* ───────────────────────────── Geometry ───────────────────────────── */

function normalizeGeometry(slots = {}) {
  const inputUnits = slots.size?.units || slots.repeat?.units || "um";
  const units = "um";

  // Two-points mode has priority if present
  if (slots.placement?.mode === "two_points" && slots.placement.p1 && slots.placement.p2) {
    const p1 = toUnits(slots.placement.p1, inputUnits);
    const p2 = toUnits(slots.placement.p2, inputUnits);
    let x = Math.min(p1.x, p2.x);
    let y = Math.min(p1.y, p2.y);
    let w = Math.abs(p2.x - p1.x);
    let h = Math.abs(p2.y - p1.y);
    ({ x, y, w, h } = snapRect({ x, y, w, h }));
    if (w <= 0 || h <= 0) throw uiError("zero_size");
    return { x, y, w, h, units };
  }

  // Otherwise need placement + size
  const sz = toSize(slots.size, inputUnits); // {w,h} // {w,h}
  const mode = slots.placement?.mode || "origin";
  const pt = slots.placement?.point || slots.placement?.center || [0, 0];
  const p = toUnits(pt, inputUnits); // {x,y}

  let x = p.x;
  let y = p.y;
  if (mode === "center") {
    x = p.x - sz.w / 2;
    y = p.y - sz.h / 2;
  }
  let { w, h } = sz;

  ({ x, y, w, h } = snapRect({ x, y, w, h }));
  if (w <= 0 || h <= 0 || !isFinite(w) || !isFinite(h)) throw uiError("zero_size");

  return { x, y, w, h, units };
}

function toSize(size, units) {
  if (!size) throw uiError("zero_size");
  if (isFinite(size.square)) {
    const s = convert(size.square, units);
    return { w: s, h: s };
  }
  const w = convert(size.w, units);
  const h = convert(size.h, units);
  if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) throw uiError("zero_size");
  return { w, h };
}

function toUnits(pair, units) {
  const [x, y] = Array.isArray(pair) ? pair : [pair?.x, pair?.y];
  return { x: convert(x, units), y: convert(y, units) };
}

function snapRect({ x, y, w, h }) {
  const xs = snap(x);
  const ys = snap(y);
  const ws = Math.max(snap(w), 0);
  const hs = Math.max(snap(h), 0);
  return { x: xs, y: ys, w: ws, h: hs };
}

function snap(n) {
  // If GridSystem exposes point/snapScalar; fallback identity if not available
  const fn = GridSystem?.snapScalar;
  return typeof fn === "function" ? fn(n) : n;
}

/* ───────────────────────────── Units ───────────────────────────── */

function convert(val, units) {
  if (!isFinite(val)) return NaN;
  const n = Number(val);
  switch ((units || "um").toLowerCase()) {
    case "nm": return n / 1000;     // store everything in µm
    case "um":
    case "µm": return n;
    case "mm": return n * 1000;
    default:   return n;            // fallback µm
  }
}

/* ───────────────────────────── Repeat ───────────────────────────── */

function normalizeRepeat(rep) {
  if (!rep) return { nx: 1, ny: 1, dx: 0, dy: 0 };
  const nx = clampInt(rep.nx ?? 1, 1, 1e6);
  const ny = clampInt(rep.ny ?? 1, 1, 1e6);
  const dx = isFinite(rep.dx) ? convert(rep.dx, rep.units || "um") : 0;
  const dy = isFinite(rep.dy) ? convert(rep.dy, rep.units || "um") : 0;
  return { nx, ny, dx, dy };
}

function clampInt(v, lo, hi) {
  const n = Math.max(lo, Math.min(hi, Math.floor(Number(v || 0))));
  return Number.isFinite(n) ? n : lo;
}

/* ───────────────────────────── Errors ───────────────────────────── */

function uiError(code) {
  return { ui: true, code };
}

export default { validateAndNormalize };
